import {
  DateTimeFormatOptions,
  formatIso8601String,
  formatNumber,
  NumericFormatOptions,
} from "@interface48/formatting";
import Tooltip from "@material-ui/core/Tooltip";
import React from "react";

interface TableCellContentProps {
  columnId: string;
  rowData: any;
  format?: {
    type: "date" | "time" | "date-time" | "numeric";
    options?: DateTimeFormatOptions | NumericFormatOptions;
  };
  emptyValue?: string;
  cellRenderer?: (cellData: any, rowData: any) => string | JSX.Element | null;
}

export const TableCellContent = (props: TableCellContentProps) => {
  const { columnId, rowData, format, emptyValue, cellRenderer } = props;

  const cellData = rowData[columnId];

  // If there is a special renderer to use, use it...
  if (cellRenderer) {
    return cellRenderer(cellData, rowData);
  }
  // Otherwise, if the cell data is null or undefined, return emptyValue or null to display nothing...
  else if (cellData == null) {
    return emptyValue ?? null;
  }
  // Otherwise, if some known format is to be applied, apply it...
  else if (format) {
    switch (format.type) {
      case "date":
      case "time":
      case "date-time":
        const options = format.options as DateTimeFormatOptions;
        // If date format, then include only the Date portion, otherwise, include all portions
        const dateTimeIncludePortions = format.type === "date" ? "date" : format.type === "time" ? "time" : undefined;
        // If we're to show relative time, then return a span containing the relative time cellData, but with a title
        // attribute containing the absolute time cellData...
        if (options && options.reference === "relative") {
          const relativeValue = formatIso8601String(cellData, dateTimeIncludePortions, options);
          const absoluteValue = formatIso8601String(cellData, dateTimeIncludePortions, {
            ...options,
            reference: "local",
          });
          return absoluteValue ? (
            <Tooltip title={absoluteValue} style={{ cursor: "help" }}>
              <span>{relativeValue}</span>
            </Tooltip>
          ) : null;
        }
        return formatIso8601String(cellData, dateTimeIncludePortions, options);
      case "numeric":
        return formatNumber(cellData, format.options as NumericFormatOptions);
      default:
        throw Error(`Table Cell Value format ${format.type} is not supported.`);
    }
  }

  // Otherwise, just return as-is...
  return cellData;
};
